import { createTheme } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface Palette {
    enkla: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
  }

  interface PaletteOptions {
    enkla?: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
  }
}

export const enklaTheme = createTheme({
  palette: {
    primary: {
      main: "#FF5F00", // Enkla orange - huvudfärg
      light: "#FF8F45", // Ljusare orange
      dark: "#E55100", // Mörkare orange
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#FFF3E0", // Mycket ljus orange/persika
      light: "#FFF8F0", 
      dark: "#FFCC80",
      contrastText: "#2E2E2E",
    },
    enkla: {
      main: "#FF5F00", // Enkla:s signaturfärg
      light: "#FF8F45",
      dark: "#E55100",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#D32F2F",
      light: "#EF5350",
      dark: "#C62828",
    },
    warning: {
      main: "#FF9800", // Orange-ton för varningar som matchar temat
      light: "#FFB74D",
      dark: "#F57C00",
    },
    info: {
      main: "#2196F3",
      light: "#64B5F6",
      dark: "#1976D2",
    },
    success: {
      main: "#4CAF50", // Grön för förnybar energi
      light: "#81C784",
      dark: "#388E3C",
    },
    text: {
      primary: "#2E2E2E", // Mjuk svart för bättre läsbarhet
      secondary: "#616161",
      disabled: "#9E9E9E",
    },
    background: {
      default: "#F6F4F0", // Crémevit/beige – matchar designens varma yta
      paper: "#FFFFFF",
    },
    divider: "rgba(255, 95, 0, 0.12)", // Orange-tonad divider
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.2,
      color: "#2E2E2E",
      letterSpacing: "-0.02em",
    },
    h2: {
      fontWeight: 600,
      fontSize: "2rem",
      lineHeight: 1.3,
      letterSpacing: "-0.01em",
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.75rem",
      lineHeight: 1.3,
      letterSpacing: "-0.01em",
    },
    h4: {
      fontWeight: 500,
      fontSize: "1.5rem",
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 500,
      fontSize: "1.25rem",
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 500,
      fontSize: "1rem",
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: "1.125rem",
      lineHeight: 1.5,
      fontWeight: 400,
    },
    subtitle2: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
      fontWeight: 500,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6, // Bättre läsbarhet
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 600,
      letterSpacing: "0.02em",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8, // Mjukare hörn
          textTransform: "none",
          fontWeight: 600,
          padding: "10px 24px",
          transition: "all 0.2s ease-in-out",
        },
        contained: {
          boxShadow: "0 2px 8px rgba(255, 95, 0, 0.25)",
          "&:hover": {
            boxShadow: "0 4px 16px rgba(255, 95, 0, 0.35)",
            transform: "translateY(-1px)",
          },
        },
        outlined: {
          borderWidth: 2,
          "&:hover": {
            borderWidth: 2,
            backgroundColor: "rgba(255, 95, 0, 0.05)",
          },
        },
        text: {
          "&:hover": {
            backgroundColor: "rgba(255, 95, 0, 0.05)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12, // Mer rundade kort
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
          transition: "box-shadow 0.2s ease-in-out",
          "&:hover": {
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          backgroundColor: "#FFFFFF",
          color: "#2E2E2E",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        filled: {
          "&.MuiChip-colorPrimary": {
            backgroundColor: "#FF5F00",
            color: "#FFFFFF",
          },
        },
        outlined: {
          "&.MuiChip-colorPrimary": {
            borderColor: "#FF5F00",
            color: "#FF5F00",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#FF8F45",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#FF5F00",
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: "#FF5F00",
          height: 3,
          borderRadius: "2px 2px 0 0",
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: "#FF5F00",
            "& + .MuiSwitch-track": {
              backgroundColor: "#FF5F00",
            },
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: "rgba(255, 95, 0, 0.2)",
        },
        barColorPrimary: {
          backgroundColor: "#FF5F00",
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          "&:before": {
            display: "none",
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 8, // Standard border radius för komponenter
  },
});

export default enklaTheme;